// cleanup_convert_to_typescript_file_PYMTLSTVIEW

/* eslint-disable import/prefer-default-export */
import { MultiSelectFilter } from '@glu/grid-react';

import formatters, {
  getValueOrDefault,
  formatCurrency
} from 'components/basicGrid/helpers/formatters';
import { dateFilterParams } from 'components/basicGrid/utils';
import {
  ACTIONS,
  AG_GRID_COLUMN_FILTER_TYPES,
  COLUMN_TYPES,
  CURRENCY,
  PAYMENT_STATUS,
  SORT_PARAMS,
  PAYMENT_SOURCE
} from 'constants.js';
import capitalize from 'utils/capitalize';
import { numberToDecimal } from 'utils/currency';
import { formatCurrencyAmount, getDecimalPlaces } from 'components/wires/utils/wire.utils';
import { PAYMENTS_COLUMNS as columns } from './web/payments.constants';

const {
  APPROVE,
  COPY_AS_PAYMENT,
  COPY_AS_TEMPLATE,
  DELETE,
  INITIATE,
  MODIFY,
  MODIFY_REVERSAL,
  REJECT,
  REVERSE,
  SEND,
  SUBMIT_FOR_APPROVAL,
  VIEW
} = ACTIONS;
const { DATE, NUMBER, TEXT } = AG_GRID_COLUMN_FILTER_TYPES;
const { DESCENDING } = SORT_PARAMS;
const { REQUIRES_OTHERS_APPROVAL } = PAYMENT_STATUS;

const CurrencyDisplay =
  (field) =>
  ({ rowData }) =>
    formatCurrency(rowData[field]);

const valueGetter = (locale, isNumber) =>
  getValueOrDefault(locale, locale['common.grid.empty'], isNumber);

const comparator = (valueA, valueB) => {
  if (valueA === valueB) {
    return 0;
  }
  return valueA > valueB ? 1 : -1;
};

const getMultiSelectFilterOptions = (data, fieldName) =>
  data
    .reduce((options, paymentItem) => {
      if (options.includes(paymentItem[fieldName])) {
        return options;
      }
      return [...options, paymentItem[fieldName]];
    }, [])
    .sort()
    .map((option) => ({
      id: option,
      name: option,
      value: option
    }));

// Determines whether the template name should be shown or not for ach payments.
// Needs to show template name for 'Recurring' for loans.
const getTemplateName =
  (locale) =>
  ({ data: { paymentEntryMethod, templateName } = {} }) =>
    paymentEntryMethod?.toLowerCase() === PAYMENT_SOURCE.TEMPLATE ||
    paymentEntryMethod?.toLowerCase() === PAYMENT_SOURCE.RECURRING
      ? templateName
      : locale['common.grid.empty'];

// lowercase mapper
export const toLowerCase = (x) => x.toLowerCase().replace(/\s/g, '');

/**
 * Determines whether action should be visible or not.
 *
 * @param {string} action payment action we are going attach
 * @param {object} props.data datasource that is binded into grid
 */
export const actionCondition =
  (action) =>
  ({ data: { possibleActions } = {} }) => {
    if (!possibleActions) {
      return false;
    }
    return (possibleActions || []).map(toLowerCase).includes(toLowerCase(action));
  };

export const paymentsActions = ({ actions, locale }) =>
  [
    {
      condition: actionCondition(VIEW),
      handler: actions?.handleView,
      label: locale['common.view']
    },
    actions && {
      condition: actionCondition(REVERSE),
      handler: actions?.handleReverse,
      label: locale['common.reverse']
    },
    actions && {
      condition: actionCondition(MODIFY_REVERSAL),
      handler: actions?.handleModifyReversal,
      label: 'Modify Reversal'
    },
    actions && {
      condition: actionCondition(APPROVE),
      handler: actions.handleApprove(APPROVE),
      label: locale['common.approve']
    },
    actions && {
      condition: actionCondition(DELETE),
      handler: actions.handleDelete(DELETE),
      label: locale['common.delete']
    },
    actions && {
      condition: actionCondition(REJECT),
      handler: actions.handleReject(REJECT),
      label: locale['common.reject']
    },
    actions && {
      condition: actionCondition(MODIFY),
      handler: actions.handleModify(MODIFY),
      label: locale['common.modify']
    },
    {
      condition: actionCondition(SEND),
      handler: actions?.handleInitiate,
      label: locale['common.send']
    },
    {
      condition: actionCondition(SUBMIT_FOR_APPROVAL),
      handler: actions?.handleInitiate,
      label: locale['common.submitForApproval']
    },
    {
      condition: actionCondition(INITIATE),
      handler: actions?.handleInitiate,
      label: locale['common.initiate']
    },
    {
      condition: actionCondition(COPY_AS_TEMPLATE),
      handler: actions?.handleCopyAsTemplate,
      label: locale['common.copyAsTemplate']
    },
    {
      condition: actionCondition(COPY_AS_PAYMENT),
      handler: actions?.handleCopyAsPayment,
      label: locale['common.copyAsNewPayment']
    }
  ].filter((x) => x);

export const paymentsBulkActions = ({ actions, locale, permissions }) =>
  [
    permissions.approve && {
      handler: actions?.handleApprove?.(APPROVE),
      label: locale['common.approve']
    },
    permissions.delete && {
      handler: actions?.handleDelete?.(DELETE),
      label: locale['common.delete']
    },
    permissions.reject && {
      handler: actions?.handleReject?.(REJECT),
      label: locale['common.reject']
    }
  ].filter((x) => x);

export const paymentsColumns = /* istanbul ignore next */ ({
  actions,
  data = [],
  fxRates,
  isApproveConfirmation,
  locale,
  permissions
}) => [
  {
    checkboxSelection: true,
    field: columns.selector,
    lockPosition: true,
    showHideDisallowed: true
  },
  {
    actions: paymentsActions({ actions, locale, permissions }),
    cellRenderer: 'actions',
    disableExport: true,
    field: columns.possibleActions,
    filterable: false,
    headerName: locale['common.actions'],
    initialWidth: 150,
    lockPosition: true,
    resizable: false,
    showHideDisallowed: true,
    sortable: false
  },
  {
    cellClassRules: {
      'alert-icon': (params) =>
        isApproveConfirmation && params?.data?.sameDay && params?.data?.newTransactionDt
    },
    field: columns.paymentType,
    filter: MultiSelectFilter,
    type: COLUMN_TYPES.MULTISELECT,
    options: getMultiSelectFilterOptions(data, columns.paymentType),
    headerName: locale['common.paymentType'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.status,
    filter: MultiSelectFilter,
    headerName: locale['common.status'],
    options: getMultiSelectFilterOptions(data, columns.status),
    sortable: true,
    ...(isApproveConfirmation && { sort: DESCENDING }),
    type: COLUMN_TYPES.MULTISELECT,
    valueFormatter: ({ data }) => {
      if (data?.status === REQUIRES_OTHERS_APPROVAL)
        return locale['paymentRule.REQUIRES_OTHERS_APPROVAL'];
      return data?.status;
    },
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.fromAccountName,
    headerName: locale['common.fromAccountName'],
    valueFormatter: ({ data }) =>
      data?.fromAccountNickName?.trim() || data?.fromAccountName?.trim() || locale['common.empty'],
    valueGetter: ({ data }) =>
      data?.fromAccountNickName?.trim() || data?.fromAccountName?.trim() || locale['common.empty']
  },
  {
    field: columns.payeeName,
    headerName: locale['common.payeeName'],
    valueGetter: valueGetter(locale)
  },
  {
    comparator,
    field: columns.custTrace,
    headerName: locale['common.customerTraceNumber'],
    valueFormatter: valueGetter(locale),
    valueGetter: valueGetter(locale, true)
  },
  {
    field: columns.transactionDt,
    filter: DATE,
    headerName: locale['common.transactionDate'],
    type: COLUMN_TYPES.DATE,
    valueFormatter: formatters.toDate({ locale, name: columns.transactionDt })
  },
  {
    cellDisplay: CurrencyDisplay(columns.amount),
    className: 'text-right',
    field: columns.amount,
    filter: NUMBER,
    filterValueGetter: ({ data }) => Number(data[columns.amount]),
    headerName: locale['common.amount'],
    initialWidth: 150,
    type: COLUMN_TYPES.NUMBER,
    variant: CURRENCY,
    valueFormatter: ({ data: { amount, currDecimals, currency } = {} } = {}) => {
      const currencyDecimalPlace = getDecimalPlaces(currency, fxRates);
      return formatCurrencyAmount(amount, currDecimals ?? currencyDecimalPlace);
    }
  },
  {
    field: columns.activationUser,
    headerName: locale['common.activationUser'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.approvalUser,
    headerName: locale['common.approvalUser'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.name,
    headerName: locale['common.paymentName'],
    valueGetter: valueGetter(locale)
  },
  ...(isApproveConfirmation
    ? [
        {
          field: columns.newTransactionDt,
          filter: DATE,
          headerName: locale['common.newTransactionDate'],
          type: COLUMN_TYPES.DATE,
          valueFormatter: formatters.toDate({ locale, name: columns.newTransactionDt }),
          valueGetter: valueGetter(locale)
        }
      ]
    : []),
  {
    field: columns.companyId,
    headerName: locale['payments.companyId'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.companyName,
    headerName: locale['payments.sender'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.companyEntryDescription,
    headerName: locale['common.paymentDescription'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.accountType,
    headerName: locale['common.accountType'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.achReceiver,
    headerName: locale['payments.achReceiver'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.activated,
    filter: DATE,
    filterParams: dateFilterParams,
    filterValueGetter: formatters.toDate({ locale, name: columns.activated }),
    headerName: locale['common.activated'],
    type: COLUMN_TYPES.DATE,
    valueFormatter: formatters.toDate({ locale, name: columns.activated }),
    valueGetter: formatters.toDate({ locale, name: columns.activated })
  },
  {
    field: columns.addendum,
    headerName: locale['common.addendaOrObi'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.approvalDate,
    filter: DATE,
    filterParams: dateFilterParams,
    filterValueGetter: formatters.toDate({ locale, name: columns.approvalDate }),
    headerName: locale['common.approvalDate'],
    type: COLUMN_TYPES.DATE,
    valueFormatter: formatters.toDate({ locale, name: columns.approvalDate }),
    valueGetter: formatters.toDate({ locale, name: columns.approvalDate })
  },
  {
    field: columns.bankTrace,
    headerName: locale['common.bankTrace'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.beneficiaryAccount,
    headerName: locale['common.beneficiaryAccount'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.beneficiary,
    headerName: locale['common.beneficiaryName'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.beneficiaryBank,
    headerName: locale['common.beneficiaryBank'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.effectiveEntryDate,
    filter: DATE,
    filterParams: dateFilterParams,
    filterValueGetter: formatters.toDate({ locale, name: columns.effectiveEntryDate }),
    headerName: locale['payments.effectiveEntryDate'],
    type: COLUMN_TYPES.DATE,
    valueFormatter: formatters.toDate({ locale, name: columns.effectiveEntryDate }),
    valueGetter: formatters.toDate({ locale, name: columns.effectiveEntryDate })
  },
  {
    field: columns.fromAccount,
    headerName: locale['common.fromAccount'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.individualIdNumber,
    headerName: locale['payments.individualIdNumber'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.individualEntryState,
    headerName: locale['payments.individualEntryState'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.itemCount,
    headerName: locale['common.totalCount'],
    type: COLUMN_TYPES.NUMBER,
    valueGetter: valueGetter(locale, true)
  },
  {
    field: columns.remainingApproval,
    headerName: locale['common.remainingApproval'],
    valueGetter: valueGetter(locale)
  },
  {
    cellDisplay: ({ data }) => (data?.sameDay ? locale['common.yes'] : locale['common.no']),
    field: columns.sameDay,
    filterValueGetter: ({ data }) => (data?.sameDay ? locale['common.yes'] : locale['common.no']),
    headerName: locale['common.sameDay'],
    type: COLUMN_TYPES.STRING,
    valueFormatter: ({ data }) => (data?.sameDay ? locale['common.yes'] : locale['common.no']),
    valueGetter: ({ data }) => data?.sameDay ?? ''
  },
  {
    field: columns.templateName,
    filter: MultiSelectFilter,
    headerName: locale['common.templateName'],
    options: getMultiSelectFilterOptions(data, columns.templateName),
    type: COLUMN_TYPES.MULTISELECT,
    valueGetter: getTemplateName(locale),
    valueFormatter: getTemplateName(locale)
  },
  {
    cellClass: 'text-right',
    cellDisplay: CurrencyDisplay(columns.numCredits),
    field: columns.numCredits,
    filter: NUMBER,
    filterValueGetter: ({ data }) => Number(data.totalCredits),
    headerName: locale['common.totalCredits'],
    type: COLUMN_TYPES.NUMBER,
    valueFormatter: ({ data }) =>
      data ? numberToDecimal(data.totalCredits) : locale['common.empty.field'],
    valueGetter: ({ data }) => Number(data.totalCredits)
  },
  {
    cellClass: 'text-right',
    cellDisplay: CurrencyDisplay(columns.numDebits),
    field: columns.numDebits,
    filter: NUMBER,
    filterValueGetter: ({ data }) => Number(data.totalDebits),
    headerName: locale['common.totalDebits'],
    type: COLUMN_TYPES.NUMBER,
    valueFormatter: ({ data }) =>
      data ? numberToDecimal(data.totalDebits) : locale['common.empty.field'],
    valueGetter: ({ data }) => Number(data.totalDebits)
  },
  {
    field: columns.paymentCategory,
    filter: TEXT,
    headerName: locale['payments.paymentCategory'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.paymentEntryMethod,
    filter: TEXT,
    headerName: locale['common.entryMethod'],
    valueFormatter: ({ value }) => capitalize(value),
    valueGetter: valueGetter(locale)
  },
  {
    comparator: (a, b, c, d) =>
      new Date(c?.data?.wireEntryDate) - new Date(d?.data?.wireEntryDate),
    field: columns.wireEntryDate,
    filter: DATE,
    filterParams: dateFilterParams,
    filterValueGetter: formatters.toDateTime({ locale, name: columns.wireEntryDate }),
    headerName: `${locale['payments.wireEntryDateTime']}`,
    type: COLUMN_TYPES.DATE,
    valueFormatter: formatters.toDateTime({ locale, name: columns.wireEntryDate }),
    valueGetter: formatters.toDateTime({ locale, name: columns.wireEntryDate })
  },
  {
    field: columns.currency,
    headerName: locale['common.currencyWord'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.memo,
    headerName: locale['common.memo'],
    valueGetter: valueGetter(locale)
  },
  {
    comparator,
    field: columns.exchangeRate,
    headerName: locale['common.exchangeRate'],
    valueGetter: valueGetter(locale)
  },
  {
    cellDisplay: CurrencyDisplay(columns.debitAmount),
    field: columns.debitAmount,
    headerName: locale['payments.fxDebitAmount'],
    type: COLUMN_TYPES.NUMBER,
    valueGetter: valueGetter(locale),
    variant: CURRENCY
  },
  {
    field: columns.confirmationInfo,
    filterable: true,
    headerName: locale['common.confirmationInfo'],
    sortable: true,
    type: COLUMN_TYPES.STRING,
    valueFormatter: ({ data }) => data?.confirmationInfo || locale['common.grid.empty'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.noteNumber,
    headerName: locale['payments.noteNumber'],
    type: COLUMN_TYPES.STRING,
    valueGetter: ({ data }) => data.noteNumber || locale['common.grid.empty']
  }
];

export const paymentConfirmationColumns = [
  columns.paymentType,
  columns.payeeName,
  columns.amount,
  columns.currency,
  columns.transactionDt,
  columns.activationUser,
  columns.exchangeRate,
  columns.debitAmount
];

export const paymentConfirmationColumnsMixed = [
  columns.paymentType,
  columns.fromAccountName,
  columns.payeeName,
  columns.amount,
  columns.currency,
  columns.transactionDt,
  columns.status,
  columns.activationUser
];

export const approvePaymentColumns = [
  columns.status,
  columns.transactionDt,
  columns.newTransactionDt,
  columns.paymentType,
  columns.payeeName,
  columns.amount,
  columns.fromAccountName,
  columns.activationUser,
  columns.custTrace,
  columns.companyEntryDescription,
  columns.currency,
  columns.exchangeRate,
  columns.debitAmount
];

export const paymentConfirmationColumnsAch = [
  columns.paymentType,
  columns.currency,
  columns.transactionDt,
  columns.amount,
  columns.custTrace,
  columns.status,
  columns.fromAccountName,
  columns.payeeName,
  columns.companyEntryDescription,
  columns.numDebits,
  columns.numCredits,
  columns.activationUser
];

export const paymentConfirmationColumnsAchReversal = [
  columns.paymentType,
  columns.transactionDt,
  columns.custTrace,
  columns.status,
  columns.payeeName,
  columns.companyEntryDescription,
  columns.numDebits,
  columns.numCredits,
  columns.activationUser
];
